// src/components/EditarAfiliadoDialog.js
//
// Diálogo administrativo de edición de datos del afiliado.
// Solo visible para SuperAdmin (el backend también lo valida — PUT /afiliados/{id}
// está restringido con [Authorize(Roles="SuperAdmin")]).
//
// Reglas (acordadas con Antonio, implementadas en AfiliadosEcolabController.Update):
//   - Si se modifica un campo LLAVE (cédula, nombres, apellidos, email, fecha nac,
//     género, tipo doc, teléfono) se exige MOTIVO obligatorio.
//   - El backend propaga los cambios al AspNetUsers vinculado y a TODAS las
//     demás afiliaciones del mismo usuario (muy auditado).
//   - Si cambia Email: EmailConfirmed=false y reenvía confirmación.
//   - Si cambia Documento: documentos aprobados vuelven a PENDIENTE para
//     re-verificación. También se valida contra ListaExclusion.
//   - HashContrato se recalcula para mantener integridad válida tras edición.

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Grid, TextField,
  MenuItem, Button, CircularProgress, IconButton, Chip, Divider, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { actualizarAfiliado } from '../services/authApi';

const CAMPOS_LLAVE = new Set([
  'documento', 'tipoDocumentoID', 'nombres', 'apellidos', 'email',
  'fechaNacimiento', 'genero', 'telefono',
]);

const GENEROS = [
  { v: 'M',  l: 'Masculino' },
  { v: 'F',  l: 'Femenino' },
  { v: 'NB', l: 'No binario' },
  { v: 'O',  l: 'Otro' },
];

const ESTADOS_CIVILES = [
  'SOLTERO', 'CASADO', 'DIVORCIADO', 'VIUDO', 'UNION_LIBRE', 'OTRO',
];

const MODALIDADES_APORTE = [
  { v: 'DINERO',  l: 'Dinero' },
  { v: 'TRABAJO', l: 'Trabajo y servicios' },
  { v: 'BIENES',  l: 'Bienes en especie' },
];

export default function EditarAfiliadoDialog({
  open, onClose, afiliado, onGuardado, onToast,
}) {
  const [form, setForm] = useState({});
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // Al abrir el diálogo, inicializar el form con los datos del afiliado
  useEffect(() => {
    if (!afiliado) return;
    setForm({
      documento:           afiliado.documento || '',
      tipoDocumentoID:     afiliado.tipoDocumentoID ?? null,
      documentoExpedidoEn: afiliado.documentoExpedidoEn || '',
      nombres:             afiliado.nombres || '',
      apellidos:           afiliado.apellidos || '',
      email:               afiliado.email || '',
      fechaNacimiento:     afiliado.fechaNacimiento || '',
      genero:              afiliado.genero || '',
      telefono:            afiliado.telefono || '',
      telefono2:           afiliado.telefono2 || '',
      paisID:              afiliado.paisID ?? null,
      departamentoID:      afiliado.departamentoID ?? null,
      municipioID:         afiliado.municipioID ?? null,
      direccion:           afiliado.direccion || '',
      codigoPostal:        afiliado.codigoPostal || '',
      estadoCivil:         afiliado.estadoCivil || '',
    });
    setMotivo('');
    setError('');
  }, [afiliado, open]);

  // Detectar qué campos cambiaron respecto al afiliado original
  const camposModificados = React.useMemo(() => {
    if (!afiliado) return [];
    const diff = [];
    const check = (key, orig, actual) => {
      const o = (orig ?? '').toString().trim();
      const a = (actual ?? '').toString().trim();
      if (o !== a) diff.push(key);
    };
    check('documento',           afiliado.documento,           form.documento);
    check('tipoDocumentoID',     afiliado.tipoDocumentoID,     form.tipoDocumentoID);
    check('documentoExpedidoEn', afiliado.documentoExpedidoEn, form.documentoExpedidoEn);
    check('nombres',             afiliado.nombres,             form.nombres);
    check('apellidos',           afiliado.apellidos,           form.apellidos);
    check('email',               afiliado.email,               form.email);
    check('fechaNacimiento',     afiliado.fechaNacimiento,     form.fechaNacimiento);
    check('genero',              afiliado.genero,              form.genero);
    check('telefono',            afiliado.telefono,            form.telefono);
    check('telefono2',           afiliado.telefono2,           form.telefono2);
    check('paisID',              afiliado.paisID,              form.paisID);
    check('departamentoID',      afiliado.departamentoID,      form.departamentoID);
    check('municipioID',         afiliado.municipioID,         form.municipioID);
    check('direccion',           afiliado.direccion,           form.direccion);
    check('codigoPostal',        afiliado.codigoPostal,        form.codigoPostal);
    check('estadoCivil',         afiliado.estadoCivil,         form.estadoCivil);
    return diff;
  }, [afiliado, form]);

  const hayCambios        = camposModificados.length > 0;
  const hayCambiosLlave   = camposModificados.some(c => CAMPOS_LLAVE.has(c));
  const motivoFaltante    = hayCambiosLlave && !motivo.trim();
  const guardarHabilitado = hayCambios && !motivoFaltante && !guardando;

  const handleGuardar = async () => {
    if (!guardarHabilitado) return;
    setError('');
    setGuardando(true);
    try {
      // Enviar solo los campos que cambiaron + motivo
      const dto = { motivo: motivo.trim() || null };
      for (const k of camposModificados) {
        dto[k] = form[k] === '' ? null : form[k];
      }
      const resp = await actualizarAfiliado(afiliado.afiliadoID, dto);
      onToast?.(
        `Afiliado actualizado. ${resp.cambios} campo(s) modificado(s).` +
        (resp.usuarioSincronizado ? ' Usuario Identity sincronizado.' : '') +
        (resp.emailConfirmacionReenviado ? ' Correo de confirmación reenviado.' : '') +
        (resp.documentosResetados > 0 ? ` ${resp.documentosResetados} documento(s) marcados para re-verificar.` : ''),
        'success'
      );
      onGuardado?.();
      onClose?.();
    } catch (e) {
      const msg = e?.data?.mensaje || e.message || 'No se pudo guardar.';
      setError(msg);
      onToast?.(msg, 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (!afiliado) return null;

  const txtProps = (campo, label, extra = {}) => ({
    label,
    size: 'small',
    fullWidth: true,
    value: form[campo] ?? '',
    onChange: (e) => setForm(f => ({ ...f, [campo]: e.target.value })),
    InputLabelProps: extra.type === 'date' ? { shrink: true } : undefined,
    disabled: guardando,
    sx: {
      '& .MuiOutlinedInput-root': {
        background: camposModificados.includes(campo) ? '#fff8e1' : '#fff',
      }
    },
    ...extra,
  });

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      {/* Header */}
      <Box sx={{ background:'linear-gradient(135deg,#1a4d2e,#2e7d32)', px:3, py:2,
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <SecurityIcon sx={{ color:'#fff' }}/>
          <Box>
            <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'1rem' }}>
              Editar datos del afiliado
            </Typography>
            <Typography sx={{ color:'rgba(255,255,255,0.85)', fontSize:'0.75rem' }}>
              Afiliado #{String(afiliado.afiliadoID).padStart(6,'0')} · {afiliado.nombres} {afiliado.apellidos}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={guardando} sx={{ color:'rgba(255,255,255,0.85)' }}>
          <CloseIcon/>
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Avisos */}
        <Alert severity="warning" icon={<WarningAmberIcon/>} sx={{ mb: 2, fontSize:'0.8rem' }}>
          <strong>Edición administrativa auditada.</strong> Los cambios en campos sensibles
          (cédula, nombres, apellidos, correo, teléfono, fecha de nacimiento, género, tipo
          de documento) se propagan al usuario de Identity y a <strong>todas las demás
          afiliaciones del mismo usuario</strong>. El hash del contrato se recalculará.
          Si cambia el correo, se reenvía confirmación. Si cambia la cédula, los
          documentos aprobados vuelven a pendiente para re-verificación.
        </Alert>

        {/* Identificación */}
        <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1a4d2e',
          textTransform:'uppercase', letterSpacing:'0.06em', mb:1 }}>
          Identificación
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField {...txtProps('nombres', 'Nombres *')}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...txtProps('apellidos', 'Apellidos *')}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...txtProps('documento', 'Cédula *')}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...txtProps('documentoExpedidoEn', 'Expedida en')}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...txtProps('fechaNacimiento', 'Fecha de nacimiento', { type:'date' })}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select {...txtProps('genero', 'Género')}>
              <MenuItem value="">—</MenuItem>
              {GENEROS.map(g => <MenuItem key={g.v} value={g.v}>{g.l}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select {...txtProps('estadoCivil', 'Estado civil')}>
              <MenuItem value="">—</MenuItem>
              {ESTADOS_CIVILES.map(e => <MenuItem key={e} value={e}>{e.replace('_',' ')}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {/* Contacto */}
        <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1a4d2e',
          textTransform:'uppercase', letterSpacing:'0.06em', mb:1 }}>
          Contacto
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField {...txtProps('email', 'Correo electrónico *', { type:'email' })}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...txtProps('telefono', 'Celular')}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...txtProps('telefono2', 'Celular 2')}/>
          </Grid>
        </Grid>

        {/* Ubicación */}
        <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1a4d2e',
          textTransform:'uppercase', letterSpacing:'0.06em', mb:1 }}>
          Ubicación
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <TextField {...txtProps('direccion', 'Dirección')}/>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...txtProps('codigoPostal', 'Código postal')}/>
          </Grid>
        </Grid>

        {/* Resumen de cambios */}
        {hayCambios && (
          <>
            <Divider sx={{ my: 2 }}/>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1a4d2e',
                textTransform:'uppercase', letterSpacing:'0.06em', mb:1 }}>
                Resumen de cambios ({camposModificados.length})
              </Typography>
              <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5 }}>
                {camposModificados.map(c => (
                  <Chip
                    key={c}
                    label={c + (CAMPOS_LLAVE.has(c) ? ' 🔑' : '')}
                    size="small"
                    color={CAMPOS_LLAVE.has(c) ? 'warning' : 'default'}
                    variant={CAMPOS_LLAVE.has(c) ? 'filled' : 'outlined'}
                    sx={{ fontSize:'0.68rem' }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label={hayCambiosLlave ? 'Motivo del cambio *' : 'Motivo del cambio (opcional)'}
              fullWidth
              multiline
              minRows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={guardando}
              error={motivoFaltante}
              helperText={
                motivoFaltante
                  ? 'El motivo es obligatorio porque estás editando campos sensibles (campos marcados con 🔑).'
                  : 'Queda registrado en la auditoría del afiliado.'
              }
            />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p:2, background:'#fafafa', borderTop:'1px solid #eee' }}>
        <Button onClick={onClose} disabled={guardando} sx={{ color:'#666' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          disabled={!guardarHabilitado}
          variant="contained"
          startIcon={guardando ? <CircularProgress size={16} color="inherit"/> : <SaveIcon/>}
          sx={{ background:'linear-gradient(135deg,#1a4d2e,#2e7d32)', '&:hover':{ background:'#143d22' } }}
        >
          {guardando ? 'Guardando…' : `Guardar ${hayCambios ? `(${camposModificados.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
